import { z } from 'zod';
import { pageQuerySchema } from './marketplace.js';
import { currencyCodeSchema, minorAmountSchema, moneySchema } from './money.js';
import { userRoleSchema, verificationStatusSchema } from './schemas.js';
/**
 * The admin console. [Admin journey, phases 1-5]
 *
 * Two rules run through all of it. Every mutation names the admin who made it,
 * because an oversight action nobody can trace is worse than no action. And
 * every list is a queue rather than a browser — an admin arrives with "what
 * needs me?", not "show me everything".
 */
/* -------------------------------------------------------------------------- */
/* Phase 1 — Access & Setup                                                   */
/* -------------------------------------------------------------------------- */
/** The dashboard snapshot. [Admin step 2] */
export const adminOverviewSchema = z.object({
    /* Queues, in the order an admin should clear them. */
    pendingVerifications: z.number().int().min(0),
    openReports: z.number().int().min(0),
    openSafetyAlerts: z.number().int().min(0),
    /** Alerts at HIGH or CRITICAL — the ones that cannot wait. */
    urgentSafetyAlerts: z.number().int().min(0),
    openDisputes: z.number().int().min(0),
    pendingModeration: z.number().int().min(0),
    flaggedPayouts: z.number().int().min(0),
    openRefunds: z.number().int().min(0),
    openSupportTickets: z.number().int().min(0),
    /* Platform size, for context rather than action. */
    totalTeens: z.number().int().min(0),
    totalParents: z.number().int().min(0),
    totalJobPosters: z.number().int().min(0),
    liveJobs: z.number().int().min(0),
    activeAssignments: z.number().int().min(0),
    restrictedUsers: z.number().int().min(0),
    /* Money, all time. */
    currency: currencyCodeSchema,
    settledVolumeMinor: minorAmountSchema,
});
/** A platform setting. [Admin step 4] */
export const systemSettingSchema = z.object({
    key: z.string(),
    value: z.string().nullable(),
    updatedAt: z.string(),
    updatedByName: z.string().nullable(),
});
export const updateSystemSettingInputSchema = z.object({
    key: z.string().trim().min(1).max(100),
    /** Null clears it back to the code default. */
    value: z.string().max(10_000).nullable(),
});
/* -------------------------------------------------------------------------- */
/* Phase 2 — User Management & Verification                                   */
/* -------------------------------------------------------------------------- */
/**
 * One account waiting on a decision. [Admin steps 5-8]
 *
 * The same shape for a parent, a teen and a job poster: an admin working a
 * queue should not have to learn three layouts to do one job.
 */
export const verificationSubjectSchema = z.object({
    /** The role profile's publicId. */
    id: z.string(),
    role: userRoleSchema,
    name: z.string(),
    email: z.string(),
    status: verificationStatusSchema,
    city: z.string().nullable(),
    state: z.string().nullable(),
    joinedAt: z.string(),
    /** Documents they have uploaded, if any. */
    documents: z.array(z.object({
        id: z.string(),
        docType: z.string(),
        docUrl: z.string(),
        status: verificationStatusSchema,
        uploadedAt: z.string(),
    })),
    /** Minors only — whether a guardian has approved them. */
    guardianApproved: z.boolean().nullable(),
});
export const verificationQueueQuerySchema = pageQuerySchema.extend({
    role: userRoleSchema.optional(),
    status: verificationStatusSchema.optional(),
});
export const verificationDecisionInputSchema = z.object({
    decision: z.enum(['APPROVE', 'REJECT']),
    /** Required on a rejection — a refusal with no reason cannot be acted on. */
    note: z.string().trim().max(2000).optional(),
});
/** A badge an admin can grant or revoke. [Admin step 9] */
export const trustBadgeSchema = z.object({
    id: z.string(),
    name: z.string(),
    criteria: z.string().nullable(),
    category: z.enum(['TRUST', 'ACHIEVEMENT']),
    /** How many live grants it has. */
    holders: z.number().int().min(0),
});
export const createTrustBadgeInputSchema = z.object({
    name: z.string().trim().min(2).max(100),
    criteria: z.string().trim().max(2000).optional(),
});
export const grantBadgeInputSchema = z.object({
    /** Any publicId identifying the person. */
    personId: z.string().uuid(),
    badgeId: z.string(),
});
/* -------------------------------------------------------------------------- */
/* Phase 3 — Content & Job Oversight                                          */
/* -------------------------------------------------------------------------- */
export const moderationStatusSchema = z.enum(['PENDING', 'APPROVED', 'REMOVED']);
export const moderationContentTypeSchema = z.enum(['JOB_POST', 'PROFILE', 'MESSAGE', 'BIO']);
/** A job as an admin sees it. [Admin steps 11-12] */
export const adminJobSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    category: z.string(),
    status: z.string(),
    posterName: z.string(),
    posterId: z.string(),
    pay: moneySchema,
    needType: z.string(),
    teensNeeded: z.number().int().min(0),
    teensAssigned: z.number().int().min(0),
    createdAt: z.string(),
    /** Set when this job has been through moderation. */
    moderationStatus: moderationStatusSchema.nullable(),
    /**
     * Why a multi-teen job might need a second look — headcount without a
     * matching budget is the pattern worth catching. [Admin step 12]
     */
    validationWarnings: z.array(z.string()),
});
export const adminJobQuerySchema = pageQuerySchema.extend({
    status: z.string().optional(),
    /** Only jobs with a validation warning. */
    needsReview: z.coerce.boolean().optional(),
});
export const moderationDecisionInputSchema = z.object({
    decision: z.enum(['APPROVE', 'REMOVE']),
    reason: z.string().trim().max(2000).optional(),
});
/** A bid, for oversight. [Admin step 13] */
export const adminBidSchema = z.object({
    id: z.string(),
    jobTitle: z.string(),
    teenName: z.string(),
    amount: moneySchema,
    /** The job's advertised budget, so an outlier is visible. */
    jobBudget: moneySchema,
    status: z.string(),
    submittedAt: z.string(),
    /** True when the bid is far outside the advertised budget. */
    outlier: z.boolean(),
});
/** A user report awaiting triage. [Admin step 14] */
export const adminReportSchema = z.object({
    id: z.string(),
    status: z.enum(['OPEN', 'UNDER_REVIEW', 'RESOLVED', 'DISMISSED']),
    reason: z.string(),
    reporterName: z.string(),
    reportedName: z.string(),
    /** The reported person's publicId, so an admin can act on them. */
    reportedPersonId: z.string(),
    jobTitle: z.string().nullable(),
    createdAt: z.string(),
    /** How many times this person has been reported before. */
    priorReports: z.number().int().min(0),
});
/**
 * Narrowing the report queue. [Admin step 14]
 *
 * Same reason as the alert queue: the list is ordered open-first then oldest,
 * so without a status filter every resolved report ever filed still occupies
 * the table an admin is trying to work through.
 */
export const adminReportQuerySchema = pageQuerySchema.extend({
    status: z.enum(['OPEN', 'UNDER_REVIEW', 'RESOLVED', 'DISMISSED']).optional(),
    /**
     * Every report about one person. The list already surfaces a prior-report
     * count as the signal worth acting on; this is how an admin then reads the
     * history behind that number.
     */
    reportedPersonId: z.uuid().optional(),
});
export const reportDecisionInputSchema = z.object({
    decision: z.enum(['UNDER_REVIEW', 'RESOLVED', 'DISMISSED']),
    note: z.string().trim().max(2000).optional(),
});
/**
 * A conversation, for safety oversight. [Admin step 15]
 *
 * Deliberately metadata only — who is talking, about what, how much. Reading a
 * minor's messages is a safeguarding action that needs a reason, so message
 * bodies are only returned for a thread an admin explicitly opens, and that
 * open is audited.
 */
export const adminConversationSchema = z.object({
    id: z.string(),
    type: z.string(),
    jobTitle: z.string().nullable(),
    participantNames: z.array(z.string()),
    /** True when a minor is in the thread. */
    involvesMinor: z.boolean(),
    /** True when a guardian is present as a reader. */
    guardianPresent: z.boolean(),
    messageCount: z.number().int().min(0),
    flaggedCount: z.number().int().min(0),
    lastMessageAt: z.string().nullable(),
});
export const adminConversationQuerySchema = pageQuerySchema.extend({
    /** Only threads with a flagged message. */
    flaggedOnly: z.coerce.boolean().optional(),
});
/** An item in the moderation queue. [Admin step 16] */
export const moderationItemSchema = z.object({
    id: z.string(),
    contentType: moderationContentTypeSchema,
    status: moderationStatusSchema,
    /** A short excerpt of what is being judged. */
    excerpt: z.string(),
    /** Who wrote it. */
    authorName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    reviewedByName: z.string().nullable(),
    reviewedAt: z.string().nullable(),
});
export const moderationQueueQuerySchema = pageQuerySchema.extend({
    status: moderationStatusSchema.optional(),
    contentType: moderationContentTypeSchema.optional(),
});
/* -------------------------------------------------------------------------- */
/* Phase 4 — Safety, Compliance & Disputes                                    */
/* -------------------------------------------------------------------------- */
/** A raised alarm, as an admin sees it. [Admin step 17] */
export const adminSafetyAlertSchema = z.object({
    id: z.string(),
    alertType: z.string(),
    severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
    status: z.enum(['OPEN', 'ACTIONED', 'CLOSED']),
    /** Who the alert is about. */
    subjectName: z.string(),
    subjectId: z.string(),
    subjectIsMinor: z.boolean(),
    jobTitle: z.string().nullable(),
    createdAt: z.string(),
    actionedByName: z.string().nullable(),
});
/**
 * Narrowing the safety queue. [Admin step 17]
 *
 * "Take immediate action" needs a way to see only what is still open. Without
 * a filter the queue mixes live alarms with everything ever closed, and the
 * oldest unactioned alert sinks below the fold as the table grows — which is
 * the one outcome a safety queue must not have.
 */
export const adminAlertQuerySchema = pageQuerySchema.extend({
    status: z.enum(['OPEN', 'ACTIONED', 'CLOSED']).optional(),
    severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']).optional(),
});
export const alertActionInputSchema = z.object({
    status: z.enum(['ACTIONED', 'CLOSED']),
    note: z.string().trim().max(2000).optional(),
});
export const policyTypeSchema = z.enum([
    'TERMS_OF_SERVICE',
    'PRIVACY_POLICY',
    'SAFETY_GUIDELINES',
    'PAYMENT_TERMS',
]);
/** A published policy version. [Admin step 18] */
export const policySchema = z.object({
    id: z.string(),
    policyType: policyTypeSchema,
    version: z.string(),
    contentUrl: z.string(),
    effectiveDate: z.string(),
    createdAt: z.string(),
    /** How many accounts have accepted this version. */
    acceptances: z.number().int().min(0),
    /** True when it is the live version for its type. */
    current: z.boolean(),
});
export const createPolicyInputSchema = z.object({
    policyType: policyTypeSchema,
    version: z.string().trim().min(1).max(20),
    contentUrl: z.string().url().max(255),
    effectiveDate: z.string().date(),
});
/** A dispute between two people. [Admin step 19] */
export const disputeSchema = z.object({
    id: z.string(),
    status: z.enum(['OPEN', 'INVESTIGATING', 'RESOLVED']),
    description: z.string(),
    jobTitle: z.string(),
    raisedByName: z.string(),
    againstName: z.string(),
    resolution: z.string().nullable(),
    resolvedByName: z.string().nullable(),
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
});
export const resolveDisputeInputSchema = z.object({
    status: z.enum(['INVESTIGATING', 'RESOLVED']),
    /** Required to resolve — closing a dispute silently helps nobody. */
    resolution: z.string().trim().max(4000).optional(),
});
/** Working limits a guardian or admin has set on a teen. [Admin step 20] */
export const teenSafetySettingSchema = z.object({
    teenId: z.string(),
    teenName: z.string(),
    maxHoursPerWeek: z.number().int().min(0).nullable(),
    allowedCategories: z.array(z.string()),
    updatedAt: z.string().nullable(),
    /** Whether the last change came from an admin or the guardian. */
    updatedBy: z.enum(['ADMIN', 'PARENT', 'NOBODY']),
});
export const updateTeenSafetyInputSchema = z.object({
    maxHoursPerWeek: z.number().int().min(0).max(168).nullable().optional(),
    allowedCategories: z.array(z.string().max(100)).max(50).optional(),
});
export const backgroundCheckStatusSchema = z.enum([
    'REQUESTED',
    'IN_PROGRESS',
    'CLEARED',
    'FLAGGED',
]);
/** A background check on a teen. [Admin step 21] */
export const backgroundCheckSchema = z.object({
    id: z.string(),
    teenId: z.string(),
    teenName: z.string(),
    provider: z.string(),
    status: backgroundCheckStatusSchema,
    resultSummary: z.string().nullable(),
    checkedAt: z.string().nullable(),
});
export const updateBackgroundCheckInputSchema = z.object({
    status: backgroundCheckStatusSchema,
    resultSummary: z.string().trim().max(4000).optional(),
});
/** A suspension or ban. [Admin step 22] */
export const restrictionSchema = z.object({
    id: z.string(),
    action: z.enum(['SUSPEND', 'BAN']),
    reason: z.string(),
    personName: z.string(),
    personId: z.string(),
    personRole: userRoleSchema,
    actionedByName: z.string(),
    startDate: z.string(),
    endDate: z.string().nullable(),
    /** False once the end date has passed. */
    active: z.boolean(),
});
export const createRestrictionInputSchema = z.object({
    personId: z.string().uuid(),
    action: z.enum(['SUSPEND', 'BAN']),
    reason: z.string().trim().min(10, 'Say why — this is on the record').max(2000),
    /** Omit for an indefinite restriction. A ban never has one. */
    endDate: z.string().date().optional(),
});
/* -------------------------------------------------------------------------- */
/* Phase 5 — Payments & Financial Oversight                                   */
/* -------------------------------------------------------------------------- */
/** Everything moving through the platform. [Admin step 23] */
export const adminPaymentOverviewSchema = z.object({
    currency: currencyCodeSchema,
    settledMinor: minorAmountSchema,
    failedMinor: minorAmountSchema,
    pendingPayoutMinor: minorAmountSchema,
    releasedPayoutMinor: minorAmountSchema,
    transactionCount: z.number().int().min(0),
    failedCount: z.number().int().min(0),
    /** Whether a provider is actually connected. */
    settlesRealMoney: z.boolean(),
    gatewayName: z.string(),
});
export const payoutStatusSchema = z.enum(['PENDING', 'APPROVED', 'RELEASED', 'FLAGGED']);
/** A payout to a teen. [Admin step 24] */
export const adminPayoutSchema = z.object({
    id: z.string(),
    teenName: z.string(),
    teenId: z.string(),
    amount: moneySchema,
    status: payoutStatusSchema,
    jobTitle: z.string().nullable(),
    releasedAt: z.string().nullable(),
    approvedByName: z.string().nullable(),
});
export const payoutQuerySchema = pageQuerySchema.extend({
    status: payoutStatusSchema.optional(),
});
export const payoutDecisionInputSchema = z.object({
    decision: z.enum(['APPROVE', 'FLAG', 'RELEASE']),
    note: z.string().trim().max(2000).optional(),
});
/** A refund or chargeback. [Admin step 25] */
export const refundSchema = z.object({
    id: z.string(),
    status: z.enum(['OPEN', 'RESOLVED', 'REJECTED']),
    reason: z.string(),
    amount: moneySchema,
    payerName: z.string(),
    payeeName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    handledByName: z.string().nullable(),
    resolvedAt: z.string().nullable(),
});
export const createRefundInputSchema = z.object({
    transactionId: z.string(),
    reason: z.string().trim().min(5).max(2000),
});
export const resolveRefundInputSchema = z.object({
    decision: z.enum(['RESOLVE', 'REJECT']),
    note: z.string().trim().max(2000).optional(),
});
/** A revenue report for a period. [Admin step 26] */
export const financialReportSchema = z.object({
    id: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    totalRevenue: moneySchema,
    totalCommission: moneySchema,
    totalPayouts: moneySchema,
    generatedAt: z.string(),
});
export const generateReportInputSchema = z.object({
    periodStart: z.string().date(),
    periodEnd: z.string().date(),
});
//# sourceMappingURL=admin.js.map